<template>

<div id="dynasty-container">

  <LoaderComponent v-if="!loaded" />
  <div v-else id="dynasty-wrapper">

    <!-- Language options bar at the top of content section -->
    <div id="languages">
      <button class="languages-button" 
        :class="{ 'language-selected': language === currentLanguage }" 
        v-for="(language, index) in languageList" :key="language" 
        @click="currentLanguage = languageList[index]">{{ fullLanguages[languageList[index]] }}
      </button>
    </div>

    <!-- Glossary "windows" -->
    <!-- 
      Each word has one window, on click the matching window
      is displayed, positioned absolutely according to click
    -->
    <div class="glossary-entry-container"
      v-for="word in glossary" :key="word.name" 
      :ref="word.name"
      :dir="rightToLeft ? 'rtl' : 'ltr'">
      <div class="glossary-close" v-if="showGlossary[word.name]" @click="hideGlossary(word.name)">
        <font-awesome-icon :icon="['fas', 'xmark']" />
      </div>
      <div class="glossary-header" v-if="showGlossary[word.name]">
        {{ $t("IslDyn_GlossPopUp_Header") }}
      </div>
      <div class="glossary-entry" v-if="showGlossary[word.name]">
        <div class="glossary-word">{{ currentWord }}</div>
        <div class="glossary-definition" v-html="word.definition"></div>
      </div>
    </div>

    <div id="dynasty">
      <BackComponent />

      <div id="dynasty-info" :dir="rightToLeft ? 'rtl' : 'ltr'">
        <div id="name" :class="{ 'info-margin': info.name2.length === 0 }" 
          v-html="info.name">
        </div>
        <div v-if="info.name2" 
          id="aka" class="info-margin" 
          v-html="`Also known as: ${info.name2}`">
        </div>
        <div v-if="info.datedescAH.length > 0 && info.datedescAD.length > 0" 
          id="dates" v-html="`${info.datedescAH} / ${info.datedescAD}`">
        </div>
        <div id="area" class="info-margin" v-html="info.area"></div>
        <div id="history" ref="description" v-html="info.history"></div>
      </div>

      <div id="related-content-container" :dir="rightToLeft ? 'rtl' : 'ltr'">
        <div id="related-header">{{ this.$i18n.t("gn_related_context").toUpperCase() }}</div>
        <div id="related-objects" :class="{ 'related-margin': !links.monuments || links.monuments.length === 0 }"
           v-if="links.objects || links.objects.length > 0">
          <router-link
            :to="{ 
              name: 'related',
              params: { id: id, type: 'objects' }
            }">
            <font-awesome-icon v-if="!rightToLeft" icon="fa-solid fa-share" />
            <font-awesome-icon v-else class="fas fa-reply" :class="{ 'fas-rtl': rightToLeft }" :icon="['fas', 'reply']" />
            {{ this.$i18n.t("item_relatedobjects") }}
          </router-link>
        </div>
        <div id="related-monuments" class="related-margin"
          v-if="links.monuments || links.monuments.length > 0">
          <router-link
            :to="{ 
              name: 'related',
              params: { id: id, type: 'monuments' }
            }">
            <font-awesome-icon v-if="!rightToLeft" icon="fa-solid fa-share" />
            <font-awesome-icon v-else class="fas fa-reply" :class="{ 'fas-rtl': rightToLeft }" :icon="['fas', 'reply']" />
            {{ this.$i18n.t("item_linkedmonuments") }}
          </router-link>
        </div>
        <div id="timeline" class="related-margin"
           v-if="links.events || links.events.length > 0">
          <router-link
            :to="{ 
              name: 'timeline',
              params: { id: id }
            }">
            <font-awesome-icon v-if="!rightToLeft" icon="fa-solid fa-share" />
            <font-awesome-icon v-else class="fas fa-reply" :class="{ 'fas-rtl': rightToLeft }" :icon="['fas', 'reply']" />
            {{ this.$i18n.t("timeline_header") }}
          </router-link>
        </div>
        <div id="artistic-introduction" class="related-margin"
          v-if="links.artisticIntro || links.artisticIntro.length > 0">
          <a :href="links.artisticIntro" target="_blank">
            <font-awesome-icon :class="{ 'fas-rtl': rightToLeft }" :icon="['fas', 'up-right-from-square']" />
            {{ this.$i18n.t("home_artistic_intro") }}
          </a>
        </div>
        <div id="exhibitions"
           v-if="links.exhibitions.length > 0">
          <div id="exhibitions-header">{{ this.$i18n.t("home_exhibitions") }}</div>
          <div>
            <div v-for="exhibition in links.exhibitions" :key="exhibition.link">
              <a :href="`${exhibition.link}?lng=${currentLanguage}`" target="_blank">
                <font-awesome-icon :class="{ 'fas-rtl': rightToLeft }" :icon="['fas', 'up-right-from-square']" />
                {{ returnValueInLanguage(currentLanguage, exhibition.name).value }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  
  </div>

</div>

</template>



<script>

import {loadLocaleMessages} from "../i18n.js";
import LoaderComponent from "../components/LoaderComponent.vue";
import BackComponent from "../components/BackComponent.vue";
import language from "../mixins/language.js";
import media from "../mixins/media.js";
export default { 
  name: "DynastyPage",
  props: ["siteLanguage"],
  components: {
    "LoaderComponent": LoaderComponent,
    "BackComponent": BackComponent
  },
  mixins: [language, media],
  data() {
    return {
      lessThan974: window.matchMedia("(max-width: 974px)").matches,
      loaded: false,
      id: this.$route.params.id,
      currentLanguage: 
        this.siteLanguage === this.$route.params.language ?
        this.siteLanguage : this.$route.params.language,
      languageList: [],
      fullLanguages: {},
      rightToLeft: false,
      allInfo: [],
      info: {},
      links: {},
      glossary: [],
      glossaryExists: false,
      showGlossary: {},
      currentWord: "",
      mwnf_website: process.env.VUE_APP_MWNF_WEBSITE,
      dia_website: process.env.VUE_APP_DIA_WEBSITE,
    }
  },
  watch: {
    "$route" (to, from) {
      loadLocaleMessages(this.siteLanguage);
    },
    "siteLanguage" () {
      this.$router.replace({ 
        name: "dynasty", 
        params: {
          id: this.$route.params.id,
          language: this.siteLanguage 
        } 
      });
      this.info = this.returnValueInLanguage(this.siteLanguage, this.allInfo);
      this.setRightToLeft(this.siteLanguage);
      this.getGlossary(this.siteLanguage);
      this.currentLanguage = this.siteLanguage;
      loadLocaleMessages(this.siteLanguage);
    },
    "currentLanguage" () {
      if (this.currentLanguage !== this.siteLanguage) {
        this.$router.replace({ 
          name: "dynasty", 
          params: {
            id: this.$route.params.id,
            language: this.currentLanguage 
          } 
        });
      }
      this.info = this.returnValueInLanguage(this.currentLanguage, this.allInfo);
      this.setRightToLeft(this.currentLanguage);
      this.getGlossary(this.currentLanguage);
    }
  },
  methods: {
    // Prop used to set conditional dir
    setRightToLeft: function(language) {
      if (language === "ar") {
        this.rightToLeft = true;
      } else {
        this.rightToLeft = false;
      }
    },
    getDynasty: function() {
      let url = process.env.VUE_APP_API_ENDPOINT + `/dynasties/${this.id}`;
      console.log(url);
      return axios({ method: "GET", "url": url }).then(result => {
        this.allInfo = result.data.details;
        this.getLanguageList(this.allInfo, "languageList");
        this.info = this.returnValueInLanguage(this.currentLanguage, this.allInfo);
        this.links = result.data.links;
        this.setRightToLeft(this.currentLanguage);
        this.getGlossary(this.currentLanguage);
        this.loaded = true;
      }, error => {
        console.error(error);
      });
    },
    getGlossary: function(language) {
      let url = process.env.VUE_APP_API_ENDPOINT + `/dynasties/${this.id}?langId=${language}`;
      return axios({ method: "GET", "url": url }).then(result => {
        if (result.data.glossary.length > 0) {
          this.glossaryExists = true;
          this.glossary = result.data.glossary;
          this.glossarySetup(this.info.history);
        } else {
          this.glossaryExists = false;
        }
      }, error => {
        console.error(error);
      });
    },
    glossarySetup: function(text) {
      // Set-up for in-description Glossary links
      // List of glossary terms (one spelling)
      let words = [];
      // Array of objects, key = word, value = array of spellings
      let spellings = [];
      // Array of regexes created from spellings array
      let regexs = [];
      if (this.glossary) {
        // Populate the showGlossary object
        // Every word gets assigned value for displaying
        // Initially false (all hidden), set to true on click
        for (let i = 0; i < this.glossary.length; i++) {
          // Empty the showGlossary object (reset)
          for (let j = 0; j < this.glossary.length; j++) {
            this.showGlossary = {};
          }
          this.$set(this.showGlossary, this.glossary[i].name, false);
        }
        // Push the names/keys for each word into words array
        for (let i = 0; i < this.glossary.length; i++) {
          words.push(this.glossary[i].name);
          let obj = {};
          // Add nested array for every word key
          obj[this.glossary[i].name] = [];
          // Push every spelling option into array
          // Remove extra whitespace characters
          for (let j = 0; j < this.glossary[i].spellings.length; j++) {
            let trimmed = this.glossary[i].spellings[j].trim();
            obj[this.glossary[i].name].push(trimmed);
          }
          // Format: [[ key: [key, ki, kee] ]]
          spellings.push(obj);
        }
        // Create array of regexs from spellings array
        for (let i = 0; i < words.length; i++) {
          let obj = {};
          // Key = word
          obj[words[i]] = [];
          // Get to array of spellings inside each word's object
          for (let j = 0; j < spellings[i][words[i]].length; j++) {
            // Change word boundaries for Arabic ([^،-٩])
            if (this.currentLanguage === "ar") {
              let regex = new RegExp(`[^،-٩]${spellings[i][words[i]][j]}[^،-٩]`, "gi");
              obj[words[i]].push(regex);
            } else {
              let regex = new RegExp(`\\b${spellings[i][words[i]][j]}\\b`, "gi");
              obj[words[i]].push(regex);
            }
          }
          regexs.push(obj);
        }
        // Add anchor links to matching Glossary words in description(s)
        if (text) {
          this.info.history = this.searchAndWrap(text, words, regexs);
        }
      }
      // If Glossary data exists, then add click events
      // to show definition window when clicking a word
      // nextTick is required due to v-html, adding event listeners
      // after Vue has updated (using refs)
      if (this.glossaryExists) {
        this.$nextTick(() => {
          if (this.info.history) {
            this.addClicksToGlossary(this.$refs.description, words, spellings, regexs);
          }
        });
      }        
    },
    getAllLanguages: function() {
      return axios({ method: "GET", "url": process.env.VUE_APP_API_ENDPOINT + "/languages" }).then(result => {
        let results = result.data;
        let list = {};
        for (let i = 0; i < results.length; i++) {
          list[results[i].langId] = results[i].value;
        }
        this.fullLanguages = list;
      }, error => {
        console.error(error);
      });
    },
    // Search provided text for regex, wrap matches in links
    searchAndWrap: function(text, words, regexs) {
      for (let i = 0; i < regexs.length; i++) {
        // Get to array of regexs inside each word's object
        for (let j = 0; j < regexs[i][words[i]].length; j++) {
          text = text.replace(regexs[i][words[i]][j], (match) => {
            return `<a class="glossary-link">${match}</a>`;
          });
        }
      }
      return text;
    },
    // Using refs, add click events to v-html text
    addClicksToGlossary: function(ref, words, spellings, regexs) {
      // Number of children (tags) inside description
      for (let i = 0; i < ref.children.length; i++) {
        for (let j = 0; j < regexs.length; j++) {
          // Get to array of regexs for each word
          for (let k = 0; k < regexs[j][words[j]].length; k++) {
            // Boolean for whether innerText of tags in description matches regex
            // Loop adds the correct click event to each instance of the matching word
            if (regexs[j][words[j]][k].test(ref.children[i].innerText)) {
              regexs[j][words[j]][k].lastIndex = 0;
              // If the element is not a.glossary-link
              // (Match triggered by i tag that contains a.glossary-link)
              if (ref.children[i].className !== "glossary-link") {
                // Check each child inside the i tag
                for (let l = 0; l < ref.children[i].children.length; l++) {
                  // Repeat of earlier test, but for inner children
                  if (regexs[j][words[j]][k].test(ref.children[i].children[l].innerText)) {
                    regexs[j][words[j]][k].lastIndex = 0;
                    // Same code as below (refactor needed) but adds listener to the child
                    ref.children[i].children[l].addEventListener("click", (e) => {
                      e.preventDefault();
                      let clickX = e.pageX;
                      let clickY = e.pageY;
                      // Larger widths: opens glossary window centered/above clicked area
                      // Smaller widths: opens glossary window in the middle of the page
                      // (top aligned with clicked area)
                      if (!this.lessThan974) {
                        this.$refs[words[j]][0].style.top = `${clickY}px`;
                        this.$refs[words[j]][0].style.left = `${clickX}px`;
                        this.$refs[words[j]][0].style.transform = `translate(-50%, -110%)`;
                      } else if (this.lessThan974) {
                        this.$refs[words[j]][0].style.top = `${clickY}px`;
                        this.$refs[words[j]][0].style.left = "50%";
                        this.$refs[words[j]][0].style.transform = "translateX(-50%)";
                      }
                      for (let m = 0; m < words.length; m++) {
                        this.showGlossary[words[m]] = false;
                      }
                      // Get the spelling of the word that matches the regex for displaying
                      this.currentWord = spellings[j][words[j]][k];
                      this.showGlossary[words[j]] = true;
                    });
                  }
                }
              } else {
                // If regex matches, add an event listener to open a glossary window
                ref.children[i].addEventListener("click", (e) => {
                  e.preventDefault();
                  let clickX = e.pageX;
                  let clickY = e.pageY;
                  if (!this.lessThan974) {
                    this.$refs[words[j]][0].style.top = `${clickY}px`;
                    this.$refs[words[j]][0].style.left = `${clickX}px`;
                    this.$refs[words[j]][0].style.transform = `translate(-50%, -110%)`;
                  } else if (this.lessThan974) {
                    this.$refs[words[j]][0].style.top = `${clickY}px`;
                    this.$refs[words[j]][0].style.left = "50%";
                    this.$refs[words[j]][0].style.transform = "translateX(-50%)";
                  }
                  for (let m = 0; m < words.length; m++) {
                    this.showGlossary[words[m]] = false;
                  }
                  // Get the spelling of the word that matches the regex for displaying
                  this.currentWord = spellings[j][words[j]][k];
                  this.showGlossary[words[j]] = true;
                });
              } 
            }
          }
        }
      }
    },
    hideGlossary: function(word) {
      this.showGlossary[word] = false;
    },
  },
  created() {
    // Insert citation link when copy/pasting from site
    window.addEventListener("copy", (e) => {
      let selection = document.getSelection();
      let citation = `\n\nSource: [${window.location.href}]`;
      e.clipboardData.setData("text/plain", selection + citation);
      e.preventDefault();
    });
  },
  mounted() {
    this.getDynasty();
    this.getAllLanguages();
    loadLocaleMessages(this.siteLanguage);
  }
}

</script>



<style scoped lang="scss">

// Size and add padding to all FA icons
// v-deep is for icons nested within v-html
#dynasty-container ::v-deep .fas {
  font-size: 75%;
  padding-right: 4px;
}
  // Reverse padding side when RTL
  #dynasty-container ::v-deep .fas-rtl .fas {
    padding-right: 0;
    padding-left: 4px;
  }

.loader-container {
  min-height: 500px;
}

#dynasty-container {
  @include flex-center;
  flex-direction: column;
  background-color: white;
  width: 100%;
}
#dynasty-wrapper {
  min-height: 500px;
  width: 100%;
}
  #dynasty {
    position: relative;
    padding: 60px;
  }

#languages {
  @include flex-center;
  justify-content: flex-end;
  background-color: $theme-dark;
}
  .languages-button {
    @include button-styles;
    color: $text-color;
    font-size: 95%;
    padding: 4px 8px;
  }
    .language-selected {
      background-color: $theme-light;
    }

.glossary-entry-container {
  width: 400px;
  position: absolute;
  z-index: 68;
  background-color: $theme-medium-dark;
  border-radius: 10px;
  color: $light-text;
}
  .glossary-close {
    @include flex-center;
    height: 30px;
    width: 30px;
    position: absolute;
    top: -10px;
    right: -15px;
    color: $text-color;
    background-color: $theme-light;
    border-radius: 100%;
  }
    // Override size and padding for FA icon
    #dynasty-container .glossary-close .fas {
      font-size: 100%;
      padding-right: 0;
    }
  .glossary-header {
    @include flex-center;
    justify-content: flex-start;
    height: 60px;
    color: $text-color;
    font-size: 110%;
    font-weight: bold;
    background-image: url("../assets/world.jpg");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: right;
    background-color: $theme-medium-dark;
    padding: 0 15px;
    border-radius: 10px 10px 0 0;
  }
  .glossary-entry {
    max-height: 150px;
    overflow: auto;
    position: relative;
    background-color: white;
    color: $text-color;
    margin: 0 15px 15px 15px;
  }
    .glossary-word {
      @include flex-center;
      justify-content: flex-start;
      height: 40px;
      color: $text-color;
      background-color: #f0ede4;
      padding: 0 15px;
    }
    .glossary-definition {
      padding: 15px;
    }

#dynasty-info {
  width: 60%;
  color: #222;
  line-height: 22px;
}
  .info-margin {
    margin-bottom: 20px;
  }
  #name {
    font-weight: bold;
  }
  #area {
    font-style: italic;
  }
  ::v-deep .glossary-link {
    @include link-styles;
    cursor: pointer;
  }
    ::v-deep .glossary-link:hover {
      @include link-hover-styles;
    }

#related-content-container {
  width: 25%;
  position: absolute;
  top: 0;
  right: 0;
  padding: 20px 20px 40px 20px;
  background-color: $theme-light;
  color: $text-color;
  line-height: 150%;
}
  .related-margin {
    margin-bottom: 10px;
  }
  #related-content-container a {
    color: $text-color;
    text-decoration: none;
  }
    #related-content-container a:hover {
      @include link-hover-styles;
    }
    #related-header {
      font-size: 110%;
      font-weight: bold;
      text-decoration: underline;
      margin: 30px 0 10px 0;
    }
    #exhibitions-header {
      font-weight: bold;
      margin-bottom: 2px;
    }
  
@media only screen and (max-width: 1249px) {
  #related-content-container {
    width: 30%;
  }
}

@media only screen and (max-width: 974px) {
  #dynasty-info {
    width: 100%;
    margin-bottom: 30px;
  }
  #related-content-container {
    width: 100%;
    position: relative;
    top: auto;
    right: auto;
    margin-bottom: 50px;
  }
    .related-header {
      font-size: 105%;
    }
}

@media only screen and (max-width: 499px) {
  .glossary-entry-container {
    width: 350px;
  }
}

@media only screen and (max-width: 399px) {
  .glossary-entry-container {
    width: 250px;
  }
}

</style>